import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Compra, ICompraPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCompras = ({
  page = 1,
  page_size = 10,
  codigo = '',
}: GetComprasParams) => {
  return useQuery({
    queryKey: ['compras', page, codigo, page_size],
    queryFn: async () => {
      return getCompras({ page, codigo, page_size });
    },
  });
};

export const useGetCompra = (id: string) => {
  return useQuery({
    queryKey: ['compras', id],
    queryFn: async () => {
      return getCompra(id);
    },
  });
};

export const useCreateCompra = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCompra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Compras'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Compra creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};


export const useUpdateCompra = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCompra,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compras'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Compras actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetComprasParams = {
  page?: number;
  page_size?: number;
  codigo?: string;
};

export type CreateCompraParams = Omit<Compra, 'id_compra'>;

export type UpdateCompraParams = {
  id: string;
  data: CreateCompraParams;
};

export const getCompras = async (params?: GetComprasParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<ICompraPagingRes>(
    `${urlBackend}api/v1/compra/?${queryParams}`,
    true
  );
};

export const getCompra = async (id: string) => {
  try{
    return await get<Compra>(`${urlBackend}api/v1/compra/${id}`, true);
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createCompra = async (data: CreateCompraParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Compra>(`${urlBackend}api/v1/compra/`, data, true);
};

export const updateCompra = async ({ id, data }: UpdateCompraParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Compra>(`${urlBackend}api/v1/compra/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};
